/**
 * AdminDITankCatalogEditor — inline pricing/badge editor for the DI Tank
 * catalog (the single source of truth at backend/data/di_tank_catalog.json).
 *
 * Lives inside /admin/di-tanks as the "Catalog Editor" tab. Edits are saved
 * via PATCH /api/admin/catalog/di-tanks/{erp} and reflect immediately on the
 * public /solutions/water-treatment and /marketplace/di-tank-exchange pages.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Loader2, Save, RefreshCw, History, DollarSign, Tag, FileText, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const EDITABLE_FIELDS = ["unit_price", "list_price", "badge", "description", "best_for"];

function formatPrice(v) {
  const n = Number(v);
  if (Number.isNaN(n)) return "—";
  return `$${n.toFixed(2)}`;
}

function formatTs(ts) {
  if (!ts) return "";
  try { return new Date(ts).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return ts; }
}

function buildDraft(product) {
  return {
    unit_price: product.unit_price ?? "",
    list_price: product.list_price ?? "",
    badge: product.badge ?? "",
    description: product.description ?? "",
    best_for: product.best_for ?? "",
  };
}

function diffPayload(draft, product) {
  const out = {};
  for (const f of EDITABLE_FIELDS) {
    const next = draft[f];
    const prev = product[f];
    if (f === "unit_price" || f === "list_price") {
      const n = next === "" || next === null ? null : Number(next);
      if (n !== null && Number.isFinite(n) && n !== Number(prev)) out[f] = n;
    } else if ((next ?? "") !== (prev ?? "")) {
      out[f] = next ?? "";
    }
  }
  return out;
}

function ProductEditCard({ product, token, onSaved }) {
  const [draft, setDraft] = useState(buildDraft(product));
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(buildDraft(product)); }, [product]);

  const pending = useMemo(() => diffPayload(draft, product), [draft, product]);
  const hasChanges = Object.keys(pending).length > 0;

  const handleSave = async () => {
    if (!hasChanges) return;
    setSaving(true);
    try {
      const res = await axios.patch(
        `${API}/admin/catalog/di-tanks/${product.erp}`,
        pending,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const updated = res.data?.product || product;
      toast.success(`${product.erp} updated · ${Object.keys(res.data?.changes || {}).length} field(s) changed`);
      onSaved(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save catalog edit");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => setDraft(buildDraft(product));

  return (
    <Card className="border-slate-200" data-testid={`catalog-edit-${product.erp}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
              <Pencil className="w-4 h-4 text-cyan-600" /> {product.name}
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">ERP: <span className="font-mono">{product.erp}</span> · {product.capacity}</p>
          </div>
          {product.badge && (
            <Badge className="bg-amber-100 text-amber-800 border-amber-200">{product.badge}</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
              <DollarSign className="w-3.5 h-3.5" /> Unit Price
            </Label>
            <Input
              type="number" step="0.01" min="0"
              value={draft.unit_price}
              onChange={(e) => setDraft((d) => ({ ...d, unit_price: e.target.value }))}
              className="mt-1"
              data-testid={`catalog-unit-price-${product.erp}`}
            />
            <p className="text-[10px] text-slate-400 mt-1">Currently: {formatPrice(product.unit_price)}</p>
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
              <DollarSign className="w-3.5 h-3.5" /> List Price
            </Label>
            <Input
              type="number" step="0.01" min="0"
              value={draft.list_price}
              onChange={(e) => setDraft((d) => ({ ...d, list_price: e.target.value }))}
              className="mt-1"
              data-testid={`catalog-list-price-${product.erp}`}
            />
            <p className="text-[10px] text-slate-400 mt-1">Currently: {formatPrice(product.list_price)}</p>
          </div>
        </div>

        <div>
          <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
            <Tag className="w-3.5 h-3.5" /> Badge
          </Label>
          <Input
            value={draft.badge}
            onChange={(e) => setDraft((d) => ({ ...d, badge: e.target.value }))}
            placeholder="e.g. Most Popular, Best Value, Starting At"
            maxLength={40}
            className="mt-1"
            data-testid={`catalog-badge-${product.erp}`}
          />
        </div>

        <div>
          <Label className="text-xs font-semibold text-slate-700">Best For</Label>
          <Input
            value={draft.best_for}
            onChange={(e) => setDraft((d) => ({ ...d, best_for: e.target.value }))}
            placeholder="High-volume SPD · Dialysis · Multi-bay lab"
            maxLength={140}
            className="mt-1"
            data-testid={`catalog-best-for-${product.erp}`}
          />
        </div>

        <div>
          <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
            <FileText className="w-3.5 h-3.5" /> Description
          </Label>
          <Textarea
            value={draft.description}
            onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
            rows={3}
            maxLength={600}
            className="mt-1 text-sm"
            data-testid={`catalog-description-${product.erp}`}
          />
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-slate-100">
          <p className="text-[11px] text-slate-500">
            {hasChanges ? <span className="text-amber-700 font-semibold">{Object.keys(pending).length} unsaved change(s)</span> : "No pending changes"}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline" size="sm"
              onClick={handleReset}
              disabled={!hasChanges || saving}
              data-testid={`catalog-reset-${product.erp}`}
            >
              Reset
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className="bg-cyan-700 hover:bg-cyan-800"
              data-testid={`catalog-save-${product.erp}`}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function AuditLogDialog({ open, onClose, token }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/admin/catalog/di-tanks/audit`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => { if (!cancelled) setHistory(res.data?.history || []); })
      .catch(() => { if (!cancelled) setHistory([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, token]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="catalog-audit-dialog">
        <DialogHeader>
          <DialogTitle>DI Tank Catalog — Edit History</DialogTitle>
          <DialogDescription>Most recent edits to the DI Tank catalog, newest first.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading audit log…
            </div>
          ) : history.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-10">No edits recorded yet.</p>
          ) : (
            <ul className="space-y-2">
              {history.map((row, idx) => (
                <li key={`${row.at}-${idx}`} className="border border-slate-200 rounded-lg p-3 bg-slate-50" data-testid={`catalog-audit-row-${idx}`}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-mono font-semibold text-slate-900">{row.erp}</span>
                    <span className="text-slate-500">{formatTs(row.at)}</span>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-0.5">{row.actor_email || "—"}</p>
                  <div className="mt-2 space-y-1">
                    {Object.entries(row.changes || {}).map(([field, ch]) => (
                      <div key={field} className="text-xs flex items-baseline gap-2">
                        <span className="font-semibold text-slate-700 capitalize">{field.replace("_", " ")}:</span>
                        <span className="text-rose-600 line-through">{String(ch.from ?? "—").slice(0, 80)}</span>
                        <span className="text-slate-400">→</span>
                        <span className="text-emerald-700 font-semibold">{String(ch.to ?? "—").slice(0, 80)}</span>
                      </div>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminDITankCatalogEditor({ token }) {
  const [products, setProducts] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [auditOpen, setAuditOpen] = useState(false);

  const fetchCatalog = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/catalog/di-tanks`);
      setProducts(res.data?.products || []);
      setMeta({
        starting_at: res.data?.starting_at,
        updated_at: res.data?.updated_at,
        currency: res.data?.currency,
        unit: res.data?.unit,
      });
    } catch {
      toast.error("Could not load DI tank catalog");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCatalog(); }, []);

  const handleProductSaved = (updated) => {
    setProducts((prev) => prev.map((p) => (p.erp === updated.erp ? updated : p)));
    fetchCatalog(); // re-pull so `updated_at` and starting_at refresh
  };

  return (
    <div className="space-y-4" data-testid="admin-di-catalog-editor">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-bold text-slate-900">DI Tank Catalog Editor</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Edits save immediately to the canonical catalog and update the public marketing &amp; marketplace pages in real time.
            {meta.updated_at && <> Last edited: <span className="font-semibold">{meta.updated_at}</span>.</>}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setAuditOpen(true)} data-testid="catalog-audit-btn">
            <History className="w-4 h-4 mr-2" /> Edit History
          </Button>
          <Button variant="outline" size="sm" onClick={fetchCatalog} data-testid="catalog-refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-500" data-testid="catalog-loading">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading catalog…
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {products.map((p) => (
            <ProductEditCard key={p.erp} product={p} token={token} onSaved={handleProductSaved} />
          ))}
        </div>
      )}

      <AuditLogDialog open={auditOpen} onClose={() => setAuditOpen(false)} token={token} />
    </div>
  );
}
